import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { NetworkHelper } from '@trading-fe/helpers';
import { SHARED_ENV } from '@trading-fe/shared';

/**
 * Corporate accounts. A company is created first; one user is then invited by email and operates
 * it. Balances, orders and every other money record belong to that user, so the money tabs of a
 * company page are the user tabs pointed at `primaryUserId`.
 *
 * Backend: `trading-be-1/modules/company/`, contract in `trading-be-1/docs/corporate-accounts.md`.
 */

export type CompanyKycStatus = 'new' | 'pending' | 'approved' | 'rejected';
export type CompanyClientStatus = 'active' | 'inactive' | 'blocked' | 'closed';
/** Emailed credentials: `pending` until the user's first sign-in, then `accepted`. */
export type CompanyInvitationStatus = 'not_sent' | 'pending' | 'accepted';

export interface CompanyFlags {
  isCounterparty: boolean;
  isIssuer: boolean;
  isOrganizationClient: boolean;
  isTaxNonResident: boolean;
  isNonResident: boolean;
  actsUnderAgencyAgreement: boolean;
  isClientBroker: boolean;
  isFinancialOrganization: boolean;
  isSoleProprietor: boolean;
  hasConflictOfInterest: boolean;
  hasSanctions: boolean;
  isPep: boolean;
  hasIndividualTariff: boolean;
  isBrokerage: boolean;
  isOrganizationEmployee: boolean;
  isFx: boolean;
  hasComplexStructure: boolean;
  isUsPerson: boolean;
  isPassiveNfe: boolean;
}

export type CompanyFlag = keyof CompanyFlags;

/** Checkbox labels, in the 1C card's order. */
export const COMPANY_FLAGS: { key: CompanyFlag; label: string }[] = [
  { key: 'isCounterparty', label: 'Counterparty' },
  { key: 'isIssuer', label: 'Issuer' },
  { key: 'isOrganizationClient', label: 'Organization client' },
  { key: 'isTaxNonResident', label: 'Not a tax resident of RA' },
  { key: 'isNonResident', label: 'Not a resident of RA' },
  { key: 'actsUnderAgencyAgreement', label: 'Acts under agency agreement' },
  { key: 'isClientBroker', label: 'Client broker' },
  { key: 'isFinancialOrganization', label: 'Financial organization' },
  { key: 'isSoleProprietor', label: 'Sole proprietor' },
  { key: 'hasConflictOfInterest', label: 'Conflict of interest' },
  { key: 'hasSanctions', label: 'Sanctions' },
  { key: 'isPep', label: 'Politically exposed person' },
  { key: 'hasIndividualTariff', label: 'Individual tariff' },
  { key: 'isBrokerage', label: 'Brokerage' },
  { key: 'isOrganizationEmployee', label: 'Organization employee' },
  { key: 'isFx', label: 'FX' },
  { key: 'hasComplexStructure', label: 'Complex structure' },
  { key: 'isUsPerson', label: 'US person' },
  { key: 'isPassiveNfe', label: 'Passive NFE' },
];

/**
 * The company's form (Application-Agreement for legal entities or for financial institutions),
 * stored as flags.isFinancialOrganization. Chosen on Company Info; the questionnaire follows it.
 */
export const COMPANY_FORMS: { value: boolean; label: string }[] = [
  { value: false, label: 'Legal entity' },
  { value: true, label: 'Financial institution' },
];

/** The flags shown as checkboxes: all but isFinancialOrganization, which is the "Form" select. */
export const COMPANY_CHECKBOX_FLAGS = COMPANY_FLAGS.filter((f) => f.key !== 'isFinancialOrganization');

/** Flags shown as warning badges on the company header and list. */
export const COMPANY_WARNING_FLAGS: { key: CompanyFlag; label: string; color: string }[] = [
  { key: 'hasSanctions', label: 'Sanctions', color: 'red' },
  { key: 'isPep', label: 'PEP', color: 'volcano' },
  { key: 'hasConflictOfInterest', label: 'Conflict of interest', color: 'orange' },
  { key: 'hasComplexStructure', label: 'Complex structure', color: 'gold' },
  { key: 'isUsPerson', label: 'US person', color: 'purple' },
  { key: 'hasIndividualTariff', label: 'Individual tariff', color: 'blue' },
];

export const COMPANY_KYC_STATUSES: { value: CompanyKycStatus; label: string; color: string }[] = [
  { value: 'new', label: 'New', color: 'default' },
  { value: 'pending', label: 'Pending', color: 'processing' },
  { value: 'approved', label: 'Approved', color: 'success' },
  { value: 'rejected', label: 'Rejected', color: 'error' },
];

/** Result of a Sumsub AML-only check (company or one person on its card). Advisory only. */
export type SumsubAmlStatus = 'not_started' | 'pending' | 'green' | 'red_retry' | 'red_final' | 'error';

export const SUMSUB_AML_STATUSES: Record<SumsubAmlStatus, { label: string; color: string }> = {
  not_started: { label: 'Not started', color: 'default' },
  pending: { label: 'In review', color: 'processing' },
  green: { label: 'Passed', color: 'success' },
  red_retry: { label: 'Failed (can retry)', color: 'warning' },
  red_final: { label: 'Failed (final)', color: 'error' },
  error: { label: 'Not submitted', color: 'error' },
};

export interface PersonSumsubKyc {
  status: SumsubAmlStatus;
  reviewStatus: string;
  reviewAnswer: string;
  reviewRejectType: string;
  rejectLabels: string[];
  comment: string;
  applicantId: string;
  submittedAt: string | null;
  reviewedAt: string | null;
  /** Why the last submit failed, e.g. "needs first and last name". */
  error: string;
}

/** One unique person on the card (the same person in several lists is one check). */
export interface SumsubAmlPerson extends PersonSumsubKyc {
  key: string;
  kind: 'individual' | 'company';
  name: string;
  roles: string[];
  /** False when the person was removed from the card or edited since it was checked. */
  onCard: boolean;
}

export interface CompanySumsubAml {
  /** Both Sumsub AML levels are configured on the backend. */
  enabled: boolean;
  started: boolean;
  startedAt: string | null;
  refreshedAt: string | null;
  company: PersonSumsubKyc;
  people: SumsubAmlPerson[];
  /** Checks with nothing submitted yet (new, edited or failed). */
  pendingSubmit: number;
}

export const COMPANY_CLIENT_STATUSES: { value: CompanyClientStatus; label: string; color: string }[] = [
  { value: 'active', label: 'Active', color: 'success' },
  { value: 'inactive', label: 'Inactive', color: 'default' },
  { value: 'blocked', label: 'Blocked', color: 'error' },
  { value: 'closed', label: 'Closed', color: 'default' },
];

// Stored as 1C's values (profile.riskProfile on users).
export const COMPANY_RISK_LEVELS: { value: string; label: string; color: string }[] = [
  { value: 'low', label: 'Low', color: 'green' },
  { value: 'mid', label: 'Medium', color: 'orange' },
  { value: 'high', label: 'High', color: 'red' },
];

export const COMPANY_INVITATION_STATUSES: Record<CompanyInvitationStatus, { label: string; color: string }> = {
  not_sent: { label: 'Not sent yet', color: 'default' },
  pending: { label: 'Not signed in yet', color: 'processing' },
  accepted: { label: 'Signed in', color: 'success' },
};

export const COMPANY_DOCUMENT_TYPES: { value: string; label: string }[] = [
  { value: 'charter', label: 'Charter' },
  { value: 'registration_certificate', label: 'Registration certificate' },
  { value: 'tax_certificate', label: 'Tax certificate' },
  { value: 'beneficial_owners', label: 'Beneficial owners' },
  { value: 'power_of_attorney', label: 'Power of attorney' },
  { value: 'financial_statements', label: 'Financial statements' },
  { value: 'other', label: 'Other' },
];

/** Loose shape check before the API's own validation. */
export const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export type CompanyContactType = 'phone' | 'email';

export const COMPANY_CONTACT_TYPES: { value: CompanyContactType; label: string }[] = [
  { value: 'phone', label: 'Phone' },
  { value: 'email', label: 'Email' },
];

/** An extra phone or email of the company; `description` is an optional note ("accountant"). */
export interface CompanyContact {
  type: CompanyContactType;
  value: string;
  description: string;
}

export interface CompanyPrimaryUser {
  id: string;
  email: string;
  status: string;
  passwordSetAt: string | null;
  invitedAt: string | null;
  lastLogin: string | null;
}

export interface CompanyInvitation {
  status: CompanyInvitationStatus;
  email: string;
  lastSentAt: string | null;
  sentCount: number;
  acceptedAt: string | null;
}

export interface Company {
  id: string;
  name: string;
  fullName: string;
  legalForm: string;
  externalId: string;
  parentCompanyId: string | null;
  tin: string;
  countryOfRegistration: string;
  registrationNumber: string;
  registrationDate: string | null;
  registrationAddress: string;
  operatingAddress: string;
  postalAddress: string;
  /** Set on create (it becomes the first user's login); cannot be changed afterwards. */
  email: string;
  contacts: CompanyContact[];
  website: string;
  /** The person the broker contacts (Application-Agreement, section 1). */
  contactPerson: CompanyContactPerson;
  license: string;
  licensingAuthority: string;
  /** Financial institutions: Legal Entity Identifier, its expiry (ISO) and the FATCA GIIN. */
  lei: string;
  leiExpiresAt: string | null;
  giin: string;
  economicSector: string;
  reportLanguage: string;
  riskLevel: string;
  identityDocument: string;
  authorizedRepresentatives: CompanyPerson[];
  clientStatus: CompanyClientStatus;
  clientStatusComment: string;
  activityField: string;
  managementTeam: CompanyPerson[];
  /** Financial institutions: the board, managementTeam's row shape. */
  boardMembers: CompanyPerson[];
  shareholders: CompanyShareholder[];
  /** Controlling beneficial owners: shareholder rows, flat (`shareholders` always empty). */
  beneficialOwners: CompanyShareholder[];
  flags: CompanyFlags;
  brokerageAccount: string;
  contractNumber: string;
  contractDate: string | null;
  kycStatus: CompanyKycStatus;
  kycRejectionReason: string;
  kycApprovedAt: string | null;
  manager: string | null;
  managerName: string | null;
  groupId: string | null;
  primaryUserId: string | null;
  /** hy / ru / en renderings; null until translated. Written only via the translations endpoints. */
  translations: CompanyTranslations | null;
  /** Sumsub AML check of the company and its people; read-only, run from the KYC tab. */
  sumsubAml: CompanySumsubAml | null;
  primaryUser: CompanyPrimaryUser | null;
  invitation: CompanyInvitation | null;
  createdBy: string | null;
  created: string;
  updated: string;
}

/** What the create / edit form sends: the editable fields only. */
/** Armenian organisational-legal forms; the value is what the API stores. */
export const COMPANY_LEGAL_FORMS: { value: string; label: string }[] = [
  { value: 'LLC', label: 'LLC (ՍՊԸ)' },
  { value: 'CJSC', label: 'CJSC (ՓԲԸ)' },
  { value: 'OJSC', label: 'OJSC (ԲԲԸ)' },
  { value: 'SOLE_PROPRIETOR', label: 'Sole proprietor (ԱՁ)' },
  { value: 'FOUNDATION', label: 'Foundation' },
  { value: 'NGO', label: 'NGO' },
  { value: 'PARTNERSHIP', label: 'Partnership' },
  { value: 'OTHER', label: 'Other' },
];

/** The company fields the backend translates, in display order. */
export const COMPANY_TRANSLATION_FIELDS: { key: string; label: string }[] = [
  { key: 'name', label: 'Name' },
  { key: 'fullName', label: 'Full name' },
  { key: 'registrationAddress', label: 'Registration address' },
  { key: 'operatingAddress', label: 'Operating address' },
  { key: 'postalAddress', label: 'Postal address' },
];

export interface CompanyTranslationSet {
  hy?: string;
  ru?: string;
  en?: string;
  source?: string;
  sourceLang?: string | null;
}

export interface CompanyTranslations {
  [field: string]: any;
  _meta?: { translatedAt?: string; model?: string; overrides?: string[] };
}

export type CompanyShareholderType = 'individual' | 'company';

/** A company shareholder lists its own shareholders; the tree is at most SHAREHOLDER_MAX_DEPTH levels. */
/** Identity document of a person: the same fields as a user's profile document. Dates are ISO. */
export interface CompanyPersonDocument {
  documentType: string;
  documentNumber: string;
  authority: string;
  issueDate: string | null;
  expireDate: string | null;
}

/** A manager or an authorized representative. */
export interface CompanyPerson extends CompanyPersonDocument {
  name: string;
  position: string;
  /** Response only; ignored on save. */
  sumsubKyc?: PersonSumsubKyc;
}

/**
 * An individual fills the document fields, a company its registration data and its own
 * shareholders; the tree is at most SHAREHOLDER_MAX_DEPTH levels.
 */
export interface CompanyShareholder extends CompanyPersonDocument {
  type: CompanyShareholderType;
  name: string;
  ownershipPercent: number | null;
  tin: string;
  registrationNumber: string;
  countryOfRegistration: string;
  shareholders: CompanyShareholder[];
  /** Beneficial owners only: why they are one (COMPANY_BENEFICIAL_OWNER_BASES). */
  basis?: CompanyBeneficialOwnerBasis[];
  /** Response only; ignored on save. */
  sumsubKyc?: PersonSumsubKyc;
}

export interface CompanyContactPerson {
  name: string;
  phone: string;
  email: string;
}

export type CompanyBeneficialOwnerBasis = 'shares_20_percent' | 'effective_control' | 'management_member';

export const COMPANY_BENEFICIAL_OWNER_BASES: { value: CompanyBeneficialOwnerBasis; label: string }[] = [
  { value: 'shares_20_percent', label: 'Holds 20% or more of shares' },
  { value: 'effective_control', label: 'Exercises actual (effective) control' },
  { value: 'management_member', label: 'Member of the management / governing body' },
];

export const COMPANY_ID_DOCUMENT_TYPES: { value: string; label: string }[] = [
  { value: 'passport', label: 'Passport' },
  { value: 'id', label: 'ID card' },
  { value: 'residence_permit', label: 'Residence permit' },
];

export const SHAREHOLDER_MAX_DEPTH = 5;

export const COMPANY_SHAREHOLDER_TYPES: { value: CompanyShareholderType; label: string }[] = [
  { value: 'individual', label: 'Individual' },
  { value: 'company', label: 'Company' },
];

export type CompanyPayload = Partial<
  Omit<
    Company,
    | 'id'
    | 'externalId'
    | 'kycStatus'
    | 'kycRejectionReason'
    | 'kycApprovedAt'
    | 'managerName'
    | 'primaryUserId'
    | 'translations'
    | 'primaryUser'
    | 'invitation'
    | 'createdBy'
    | 'created'
    | 'updated'
  >
> & {
  /** Create only: email the login and password now (default) or leave it for the Users tab. */
  sendCredentials?: boolean;
};

export interface CompanySearchParams {
  page: number;
  itemsPerPage: number;
  search?: string;
  kycStatus?: 'any' | CompanyKycStatus;
  clientStatus?: 'any' | CompanyClientStatus;
  riskLevel?: string;
  invitationStatus?: 'any' | CompanyInvitationStatus;
  sortBy?: string;
}

export interface CompanySearchResponse {
  companies: Company[];
  total: number;
}

export interface CompanyDocumentUpload {
  key: string;
  fileName: string;
  documentType: string;
  comment?: string;
}

export type CompanyQuestionnaireType = 'legal_entity' | 'financial_institution';

export interface QuestionnaireResidency {
  country: string;
  tin: string;
  /** 'A' the country issues no TINs, 'B' unable to obtain one (explain). */
  noTinReason: '' | 'A' | 'B';
  explanation: string;
}

export interface QuestionnaireAddress {
  country: string;
  region: string;
  street: string;
  postalCode: string;
}

/** Annex 1: the tax self-certification of one controlling person of a passive NFE. */
export interface QuestionnaireControllingPerson {
  firstName: string;
  lastName: string;
  residenceAddress: QuestionnaireAddress;
  mailingAddress: QuestionnaireAddress;
  birthDate: string | null;
  birthCountry: string;
  birthCity: string;
  residencies: QuestionnaireResidency[];
  controlType: string;
  signedBy: string;
  capacity: string;
}

/**
 * The Application-Agreement answers that are not profile data. Mirrors `SPEC` in
 * trading-be-1/modules/company/company-questionnaire.model.js; yes/no answers are null until answered.
 */
export interface CompanyQuestionnaireAnswers {
  type: CompanyQuestionnaireType;
  business: {
    description: string;
    employeesCount: number | null;
    mainActivities: string;
    mainServices: string;
    serviceCountries: string;
    mainPartners: string;
    partnerCountries: string;
    customerBaseCountries: string;
    participations: { name: string; sharePercent: number | null; country: string }[];
  };
  pep: { present: boolean | null; names: string; familyMember: boolean | null };
  income: { source: string; range: string }[];
  financials: {
    year: number | null;
    currency: string;
    assets: number | null;
    profit: number | null;
    capital: number | null;
  };
  investment: { expectedTotal: string; period: string; accountType: string };
  transactionCountries: string[];
  transactionCountriesOther: string;
  banking: {
    bankName: string;
    bankCountry: string;
    bankAccountNumber: string;
    brokerageName: string;
    brokerageCountry: string;
    brokerageAccountNumber: string;
  };
  tax: {
    entityType: string;
    securitiesMarket: string;
    residencies: QuestionnaireResidency[];
    controllingPersons: QuestionnaireControllingPerson[];
  };
  usPerson: string;
  russianStatus: { entityRegisteredInRussia: boolean | null; beneficialOwner: string[] };
  aml: Record<string, any>;
  /** "Preferred language for communication and reporting": the company's reportLanguage. */
  preferredLanguage: string;
  signingBasis: { basis: string; other: string };
  declarations: Record<string, boolean | null>;
  signatory: { name: string; position: string; signedAt: string | null };
}

export interface CompanyQuestionnaire extends CompanyQuestionnaireAnswers {
  /** null until first saved. */
  id: string | null;
  companyId: string;
  updatedBy: string | null;
  updatedByName: string;
  completedAt: string | null;
  completedBy: string | null;
  completedByName: string;
  created?: string;
  updated?: string;
}

@Injectable({ providedIn: 'root' })
export class CompanyService {
  private readonly env = inject(SHARED_ENV);
  private readonly networkHelper = inject(NetworkHelper);
  /** `env.host` already ends in `/api`. */
  private readonly baseUrl = `${this.env.host}/admin/companies`;

  private url(id: string, suffix = ''): string {
    return `${this.baseUrl}/${encodeURIComponent(id)}${suffix}`;
  }

  search(params: CompanySearchParams): Observable<CompanySearchResponse> {
    return this.networkHelper.post(`${this.baseUrl}/search`, params);
  }

  get(id: string): Observable<Company> {
    return this.networkHelper.get(this.url(id));
  }

  /**
   * Also creates the company's user from `payload.email` and emails its password.
   * `credentialsEmailFailed` is set when both were saved but that email could not be sent.
   */
  create(payload: CompanyPayload): Observable<Company & { credentialsEmailFailed?: boolean }> {
    return this.networkHelper.post(this.baseUrl, payload);
  }

  update(id: string, payload: CompanyPayload): Observable<Company> {
    return this.networkHelper.put(this.url(id), payload);
  }

  /** Partial: only the leaves being changed; '' resets a leaf. */
  editTranslations(
    id: string,
    translations: Record<string, Record<string, string>>
  ): Observable<{ set: string[]; cleared: string[]; translations: CompanyTranslations }> {
    return this.networkHelper.put(this.url(id, '/translations'), { translations });
  }

  /** Re-run the machine translation; `force` discards manual corrections. */
  refreshTranslations(
    id: string,
    payload: { force?: boolean; fields?: string[] } = {}
  ): Observable<any> {
    return this.networkHelper.post(this.url(id, '/translations'), payload);
  }

  setKycStatus(
    id: string,
    kycStatus: Exclude<CompanyKycStatus, 'new'>,
    reason?: string
  ): Observable<Company> {
    return this.networkHelper.put(this.url(id, '/kyc-status'), {
      kycStatus,
      ...(reason ? { reason } : {}),
    });
  }

  /** Staff notes on the company, newest first (the company counterpart of user comments). */
  getComments(id: string): Observable<any[]> {
    return this.networkHelper.get(this.url(id, '/comments'));
  }

  /** The questionnaire; an empty one (`id: null`) of the company's default type when none was saved. */
  getQuestionnaire(id: string): Observable<CompanyQuestionnaire> {
    return this.networkHelper.get(this.url(id, '/questionnaire'));
  }

  /** Replaces every answer; the backend re-derives the PEP / US / passive NFE / FI flags. */
  saveQuestionnaire(id: string, answers: CompanyQuestionnaireAnswers): Observable<CompanyQuestionnaire> {
    return this.networkHelper.put(this.url(id, '/questionnaire'), answers);
  }

  /** Stamps the saved answers complete; a 400 lists what is missing. */
  completeQuestionnaire(id: string): Observable<CompanyQuestionnaire> {
    return this.networkHelper.post(this.url(id, '/questionnaire/complete'), {});
  }

  /** Append a staff note; comments cannot be edited or deleted. */
  addComment(id: string, text: string): Observable<any> {
    return this.networkHelper.post(this.url(id, '/comments'), { text });
  }

  /**
   * Run the Sumsub AML check for the company and every person on its card. Idempotent: people
   * already submitted are skipped, so it also submits new / edited people and retries failed ones.
   */
  startSumsubAml(id: string): Observable<Company> {
    return this.networkHelper.post(this.url(id, '/sumsub-aml'), {});
  }

  /** Re-read the Sumsub status of every stored applicant. */
  refreshSumsubAml(id: string): Observable<Company> {
    return this.networkHelper.post(this.url(id, '/sumsub-aml/refresh'), {});
  }

  getDocuments(id: string): Observable<any[]> {
    return this.networkHelper.get(this.url(id, '/documents'));
  }

  /** Same response as the user presign-upload: `{ key, url, fields, maxBytes, allowedContentTypes }`. */
  presignDocumentUpload(id: string, fileName: string): Observable<any> {
    return this.networkHelper.post(this.url(id, '/documents/presign-upload'), {
      fileName,
    });
  }

  addDocument(id: string, data: CompanyDocumentUpload): Observable<any> {
    return this.networkHelper.post(this.url(id, '/documents'), data);
  }

  /** A 15-minute download link, scoped to the company's own documents. */
  documentDownloadUrl(id: string, documentId: string): Observable<{ key: string; url: string }> {
    return this.networkHelper.post(
      this.url(id, `/documents/${encodeURIComponent(documentId)}/presign-download`),
      {}
    );
  }

  deleteDocument(id: string, documentId: string): Observable<{ success: boolean }> {
    return this.networkHelper.delete(
      this.url(id, `/documents/${encodeURIComponent(documentId)}`)
    );
  }

  /** Creates the company's user and emails it a generated password. */
  invite(id: string, email: string, lang: string): Observable<Company> {
    return this.networkHelper.post(this.url(id, '/user'), { email, lang });
  }

  /** Emails a new generated password; only while the user has not set its own. */
  resendCredentials(id: string): Observable<Company> {
    return this.networkHelper.post(this.url(id, '/user/resend-credentials'), {});
  }

  /** Removes a user that never signed in and has no money records. */
  removeUser(id: string): Observable<Company> {
    return this.networkHelper.delete(this.url(id, '/user'));
  }
}
